import {
  Button,
  GroupBox,
  MessageBox,
  Paragraph,
  Toasts,
  Tooltip,
} from '@infotech-soe/erp-ui-kit';
import { API_ERRORS } from '@infotech-soe/merps/types/errors';
import React, { memo, MouseEvent, useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { ErrorNotificationKey } from '../../../types';
import { copyTextToClipboard } from '../dataset/utils/copyTextToClipboard';
import { LoginError } from '../session/hooks/useLoginFormState';

import ErrorDetails from './ErrorDetails';
import { errorMessages, messages } from './messages';
import { useStyles } from './styles';
import { ErrorNotificationProps } from './types';
import { useLocalizedMessage } from './utils';

const errorMap = new Map<LoginError, ErrorNotificationKey>([
  [LoginError.CHANGE_PASSWORD, 'needChangePass'],
  [LoginError.SAME_PASSWORD, 'samePasswords'],
  [LoginError.WEAK_PASSWORD, 'weakPassword'],
  [LoginError.REQUIRED_SMART_CARD, 'smartCardAuthRequired'],
  [LoginError.WRONG_USERNAME_OR_PASSWORD, 'wrongUsernameOrPassword'],
]);

const ErrorNotification = (props: ErrorNotificationProps) => {
  const { name, id, code, message, stack, info, logout, onSubmit, open } =
    props;
  const { formatMessage } = useIntl();
  const classes = useStyles();
  const [isCopyToastOpen, setCopyToastOpen] = useState(false);

  const handleSubmit = useCallback(() => {
    onSubmit();
    if (logout) window.location.reload();
  }, [logout, onSubmit]);

  const translatedInfo = useMemo((): {
    message: string;
    stack: string | null;
  } => {
    if (code === LoginError.WEAK_PASSWORD) {
      return { message, stack };
    } else if (code === API_ERRORS.DATABASE_ERROR) {
      const [dbMessage, ...dbStack] = message.split('\n');
      return { message: dbMessage, stack: dbStack.join('\n') };
    } else if (code && errorMap.has(code)) {
      const messageId = errorMap.get(code);
      return {
        message: formatMessage(
          (messageId && errorMessages[messageId]) ?? errorMessages.systemError
        ),
        stack,
      };
    } else return { message: info ?? message, stack };
  }, [code, message, info, stack, formatMessage]);

  const localizedMessage = useLocalizedMessage({
    code,
    info,
    id,
    message,
    formatMessage,
  });

  const handleCopy = useCallback(
    async (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      await copyTextToClipboard(
        `${formatMessage(messages.title)}:\n  ${localizedMessage}\n` +
          `\n${formatMessage(messages.details)}:\n` +
          `  ${formatMessage(messages.type, { type: name })}\n` +
          (code && `  ${formatMessage(messages.code, { code })}\n`) +
          `  ${formatMessage(messages.message, { message: translatedInfo.message })}\n` +
          (translatedInfo.stack &&
            `\n${formatMessage(messages.stack)}:\n${translatedInfo.stack
              .split('\n')
              .map(line => `  ${line}`)
              .join('\n')}`)
      );
      setCopyToastOpen(true);
    },
    [code, formatMessage, localizedMessage, name, translatedInfo]
  );

  const detailslegend = useMemo(
    () => (
      <GroupBox.Legend>
        {formatMessage(messages.details)}
        <Tooltip label={formatMessage(messages.copy)} delay={500}>
          <Button icon="duplicate" variant="borderless" onClick={handleCopy} />
        </Tooltip>
      </GroupBox.Legend>
    ),
    [formatMessage, handleCopy]
  );
  return (
    <MessageBox
      className={classes.dialog}
      height="auto"
      noCloseButton={false}
      open={open}
      resizable
      y="2%"
      title={formatMessage(messages.title)}
      onClose={onSubmit}
    >
      <MessageBox.Content data-modal-skip-initial-focus className="m-pa-md">
        <Paragraph className="m-px-md">{localizedMessage}</Paragraph>
        <ErrorDetails
          legend={detailslegend}
          name={name}
          code={code}
          message={translatedInfo.message}
          stack={translatedInfo.stack}
        />
      </MessageBox.Content>
      <MessageBox.Footer>
        <MessageBox.Button
          label={formatMessage(messages.submit)}
          sx={{ width: 60 }}
          type="submit"
          onClick={handleSubmit}
        />
      </MessageBox.Footer>
      <Toasts open={isCopyToastOpen}>
        <Toasts.Toast
          autoDismissTimeOut={1000}
          className={classes.toast}
          type="success"
          onClose={() => setCopyToastOpen(false)}
        >
          {formatMessage(messages.copied)}
        </Toasts.Toast>
      </Toasts>
    </MessageBox>
  );
};

export default memo(ErrorNotification);
